import SEO from '../components/SEO';
import React from 'react';
import Navigation from '../components/Navigation';
import SiteFooter from '../components/SiteFooter';

export default function AboutPage() {
  return (
    <div className="flex flex-col min-h-screen bg-white">
      <SEO title="About Us" description="Over 30 years of benefits expertise. GetBenefitSavings helps employees understand and maximize their benefits package, from 401k to health insurance." path="/about" keywords="benefits expert, employee benefits consulting, Vonnie Mattei, benefits optimization, compensation analysis" />
      
      <Navigation />
      
      <section className="bg-gradient-hero py-20 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">About GetBenefitSavings</h1>
          <p className="text-xl md:text-2xl opacity-90 max-w-2xl mx-auto">
            Expert guidance to maximize your benefits and savings
          </p>
        </div>
      </section>

      <section className="py-16 bg-white flex-grow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Mission Section */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h2>
            <p className="text-lg text-gray-700 mb-4">
              GetBenefitSavings helps employees navigate the complex world of credit card benefits and employee perks to maximize their savings and make smarter financial decisions. With over 30 years of benefits expertise, we provide the guidance you need to unlock thousands of dollars in value you may be leaving on the table.
            </p>
          </div>

          {/* Founder Section */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Meet Vonnie Mattei</h2>
            
            <div className="bg-gray-50 p-8 rounded-2xl border border-gray-200">
              <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="flex-shrink-0">
                  <div className="w-32 h-32 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-4xl font-bold text-blue-600">VM</span>
                  </div>
                </div>
                
                <div className="flex-grow">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Vonnie Mattei</h3>
                  <p className="text-blue-600 font-semibold mb-2">Founder & Benefits Expert</p>
                  <p className="text-gray-600 mb-4">Certified Compensation Professional (CCP)</p>
                  
                  <p className="text-gray-700 mb-4">
                    Vonnie brings over 30 years of expertise in benefits management, having worked with some of the world's leading companies including Tesla, SanDisk, United Healthcare, and Amyris. Her diverse background spans Electronic Vehicles/Energy, Biotechnology, Healthcare, Corporate Consulting, High-Tech, Retail, and Manufacturing industries.
                  </p>
                  
                  <div className="bg-white p-4 rounded-lg border border-gray-200 mb-4">
                    <h4 className="font-bold text-gray-900 mb-3">Career Highlights:</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 font-bold mt-1">✓</span>
                        <span><strong>Tesla Benefits Manager:</strong> Managed benefits for over 80,000 employees, saving the company $20M through strategic dependent verification</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 font-bold mt-1">✓</span>
                        <span><strong>United Healthcare:</strong> Benefits Account Manager serving key accounts and developing comprehensive healthcare solutions</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 font-bold mt-1">✓</span>
                        <span><strong>Cost Savings Expert:</strong> Achieved three consecutive years of premium decreases while maintaining market-leading benefits at Amyris</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 font-bold mt-1">✓</span>
                        <span><strong>Certified Professional:</strong> Certified Compensation Professional (CCP) with proven expertise in strategic benefits optimization</span>
                      </li>
                    </ul>
                  </div>
                  
                  <p className="text-gray-700 mb-4">
                    With hands-on experience in end-to-end benefits management, vendor negotiations, employee education, and compliance across multiple states and countries, Vonnie understands exactly how employees can maximize their benefits—and how much money is often left unclaimed.
                  </p>
                  
                  <div className="mt-6">
                    <a 
                      href="https://linkedin.com/in/vonniemattei" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z"/>
                      </svg>
                      Connect on LinkedIn
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Why We Do This Section */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Why GetBenefitSavings Exists</h2>
            <p className="text-lg text-gray-700 mb-4">
              After three decades of managing benefits for thousands of employees across major corporations, Vonnie saw the same pattern repeatedly: <strong>employees leave thousands of dollars on the table every year</strong> simply because they don't know about all the credit card benefits and employee perks available to them.
            </p>
            <p className="text-lg text-gray-700 mb-4">
              From premium travel insurance included with credit cards to forgotten FSA balances, from unused wellness credits to overlooked employer matching programs—the amount of unclaimed value is staggering.
            </p>
            <p className="text-lg text-gray-700">
              GetBenefitSavings was created to change that. Through clear guidance, expert insights, and easy-to-use tools, we help you unlock the full value of what you already have—so you can save smarter and spend wiser.
            </p>
          </div>

          {/* Expertise Section */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Expertise</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Benefits Optimization</h3>
                <p className="text-gray-700">Strategic guidance on maximizing credit card perks, FSA/HSA accounts, and employee benefits programs.</p>
              </div>
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Cost Savings Analysis</h3>
                <p className="text-gray-700">Expert analysis to identify hidden savings opportunities and unclaimed benefits worth thousands.</p>
              </div>
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Employee Education</h3>
                <p className="text-gray-700">Clear, actionable education on complex benefits topics made simple and accessible.</p>
              </div>
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Strategic Planning</h3>
                <p className="text-gray-700">Long-term financial strategies to maximize benefits throughout your career.</p>
              </div>
            </div>
          </div>

        </div>
      </section>

      
      <SiteFooter />
    </div>
  );
}
